/* eslint-disable max-len */
/** StockDNA — UI label translations EN/ID. */
export const STRINGS = {
    en: {
        title: "DISCOVER YOUR INVESTOR PERSONALITY",
        powered_by: "Developed & powered by",
        hero_desc: "Your <em>personality</em> already knows which stocks fit you. Our psychometric quiz — built on Nobel Prize-winning behavioral finance research — decodes your investor DNA in just 5 minutes.",
        market_label: "SELECT YOUR STOCK MARKET",
        market_idx: "Indonesia Stocks",
        market_global_label: "Global",
        market_global_sub: "US / International",
        market_both_label: "Both",
        market_both_sub: "IDX + Global Mix",
        cta_start: "DECODE MY STOCKDNA →",
        stat_types: "Investor Types",
        stat_questions: "Questions",
        stat_time: "To Your Result",
        stat_coverage: "Stock Coverage",
        research_basis: "Research-Based Framework",
        research_body: "Questions grounded in Kahneman & Tversky's Prospect Theory (1979), Barber & Odean (2001), Markowitz's Portfolio Theory (1952), Klontz's Money Scripts (2011), and Mayfield et al.'s Big Five personality-investing correlations (2008).",
        back: "BACK",
        next: "NEXT →",
        see_result: "SEE MY STOCKDNA →",
        question_of: (n, total) => `QUESTION ${n} OF ${total}`,
        loading_title: "Sequencing your StockDNA...",
        loading_msgs: ["ANALYSING PERSONALITY TRAITS", "MAPPING RISK TOLERANCE", "IDENTIFYING STOCK ARCHETYPES", "SEQUENCING YOUR DNA PROFILE"],
        result_eyebrow: "YOUR STOCKDNA RESULT",
        risk_tolerance: "RISK TOLERANCE",
        section_stocks: "YOUR MATCHED STOCKS",
        section_insights: "INVESTOR INSIGHTS",
        section_research: "RESEARCH BASIS",
        section_thoughts: "YOUR THOUGHTS",
        section_deeper: "GO DEEPER WITH YOUR DNA",
        section_share: "SHARE YOUR RESULT",
        refs_title: "Scientific References",
        comment_prompt: "Does this match your investing style? Share your reaction — when ready to go deeper, our professional tools can run a full analysis on any stock that caught your eye.",
        comment_ph: "e.g. I think I'm more conservative... but BBCA has been on my watchlist for months.",
        save_thoughts: "SAVE MY THOUGHTS",
        saved_ok: "Saved! Choose your analysis tool below.",
        urgency: "INVESTORS WHO ANALYSE THEIR STOCKS BUILD STRONGER CONVICTION",
        cta_headline: "Your DNA is decoded. Now let's act on it.",
        cta_sub: "You've just discovered <strong>how you think about money.</strong> Knowing your type is step one. The investors who beat the market combine personality insight with <strong>hard data and disciplined analysis.</strong>",
        for_adults: "FOR ADULTS",
        adult_desc: "Professional AI analysis. Financials, technicals, sentiment, AI-powered thesis — one dashboard.",
        analyse_now: "Analyse Now",
        for_kids: "FOR CHILDREN & YOUNG INVESTORS",
        kids_desc: "Investing made fun and visual for the next generation. Gamified, safe, builds wealth habits early.",
        start_learning: "Start Learning",
        proof: 'Both tools are <span>free to try</span> · No credit card · Trusted by investors across Indonesia & globally',
        share: "⬡ SHARE",
        copy_result: "COPY FULL RESULT (TEXT)",
        export_pdf: "EXPORT AS PDF",
        retake: "↺ RETAKE QUIZ",
        save_warning_title: "Save your result — or you'll have to retake the quiz",
        save_warning_body: "We don't store your StockDNA on our servers. The PDF export is the only way to keep a permanent, shareable copy of your full result. Refreshing or closing this page will lose it.",
        back_to_home: (host) => `Back to ${host}`,
        disclaimer: "Not financial advice · Educational purposes only · © 2026 NeuLab Inc.",
    },
    id: {
        title: "TEMUKAN KEPRIBADIAN INVESTOR ANDA",
        powered_by: "Dikembangkan & didukung oleh",
        hero_desc: "<em>Kepribadian</em> Anda sudah tahu saham mana yang cocok. Kuis psikometrik kami — berdasarkan penelitian keuangan perilaku pemenang Nobel — mendekode DNA investor Anda hanya dalam 5 menit.",
        market_label: "PILIH PASAR SAHAM ANDA",
        market_idx: "Saham Indonesia",
        market_global_label: "Global",
        market_global_sub: "AS / Internasional",
        market_both_label: "Keduanya",
        market_both_sub: "IDX + Campuran Global",
        cta_start: "DEKODE STOCKDNA SAYA →",
        stat_types: "Tipe Investor",
        stat_questions: "Pertanyaan",
        stat_time: "Ke Hasil Anda",
        stat_coverage: "Cakupan Saham",
        research_basis: "Kerangka Berbasis Riset",
        research_body: "Pertanyaan didasarkan pada Teori Prospek Kahneman & Tversky (1979), studi Barber & Odean (2001), Teori Portofolio Markowitz (1952), Naskah Uang Klontz (2011), dan korelasi kepribadian Big Five Mayfield et al. (2008).",
        back: "KEMBALI",
        next: "BERIKUTNYA →",
        see_result: "LIHAT STOCKDNA SAYA →",
        question_of: (n, total) => `PERTANYAAN ${n} DARI ${total}`,
        loading_title: "Menganalisis StockDNA Anda...",
        loading_msgs: ["MENGANALISIS SIFAT KEPRIBADIAN", "MEMETAKAN TOLERANSI RISIKO", "MENGIDENTIFIKASI ARKETIPE SAHAM", "MENGURUTAN PROFIL DNA ANDA"],
        result_eyebrow: "HASIL STOCKDNA ANDA",
        risk_tolerance: "TOLERANSI RISIKO",
        section_stocks: "SAHAM YANG COCOK UNTUK ANDA",
        section_insights: "WAWASAN INVESTOR",
        section_research: "DASAR PENELITIAN",
        section_thoughts: "PENDAPAT ANDA",
        section_deeper: "DALAMI DNA ANDA",
        section_share: "BAGIKAN HASIL ANDA",
        refs_title: "Referensi Ilmiah",
        comment_prompt: "Apakah ini sesuai dengan gaya investasi Anda? Bagikan reaksi Anda — saat siap mendalami lebih lanjut, alat kami dapat menjalankan analisis penuh pada saham yang menarik perhatian Anda.",
        comment_ph: "misal: Saya pikir saya lebih konservatif... tapi BBCA sudah lama ada di watchlist saya.",
        save_thoughts: "SIMPAN PENDAPAT SAYA",
        saved_ok: "Tersimpan! Pilih alat analisis Anda di bawah.",
        urgency: "INVESTOR YANG MENGANALISIS SAHAM MEREKA MEMBANGUN KEYAKINAN YANG LEBIH KUAT",
        cta_headline: "DNA Anda telah terdekode. Sekarang saatnya bertindak.",
        cta_sub: "Anda baru saja menemukan <strong>cara Anda berpikir tentang uang.</strong> Mengetahui tipe Anda hanyalah langkah pertama.",
        for_adults: "UNTUK DEWASA",
        adult_desc: "Analisis saham AI profesional. Keuangan, teknikal, sentimen, tesis AI — satu dashboard.",
        analyse_now: "Analisis Sekarang",
        for_kids: "UNTUK ANAK & INVESTOR MUDA",
        kids_desc: "Jadikan investasi menyenangkan untuk generasi berikutnya. Gamifikasi, aman, membangun kebiasaan kekayaan sejak dini.",
        start_learning: "Mulai Belajar",
        proof: 'Kedua alat <span>gratis dicoba</span> · Tanpa kartu kredit · Dipercaya investor di Indonesia & dunia',
        share: "⬡ BAGIKAN",
        copy_result: "SALIN HASIL LENGKAP (TEKS)",
        export_pdf: "EKSPOR SEBAGAI PDF",
        retake: "↺ ULANGI KUIS",
        save_warning_title: "Simpan hasil Anda — atau Anda harus mengulang kuis",
        save_warning_body: "Kami tidak menyimpan StockDNA Anda di server. Ekspor PDF adalah satu-satunya cara untuk menyimpan salinan permanen hasil lengkap Anda. Refresh atau menutup halaman akan menghilangkan hasilnya.",
        back_to_home: (host) => `Kembali ke ${host}`,
        disclaimer: "Bukan saran keuangan · Hanya untuk tujuan pendidikan · © 2026 NeuLab Inc.",
    },
};
